import { useCallback, useEffect, useRef, useState } from "react";
import { formatIndianMoney } from "../utils/money";
import { isFreeCheckout, normalizeModuleIds, razorpayAmountPaise } from "../utils/purchase";
import { apiFetch } from "../utils/api";
import { USER_BASE_URL, VERSION } from "../utils/config";
import { showToast } from "../toastService";
import "../styles/PaymentSheet.css";

export default function PaymentSheet({
  open,
  onClose,
  courseDetails,
  buyFullCourse = false,
}) {
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [discountText, setDiscountText] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  const payable = Math.max(0, (courseDetails?.price || 0) - (buyFullCourse ? 0 : discount));

  const stopStatusPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  const completePurchase = useCallback((message = "Payment successful! 🎉") => {
    stopStatusPolling();
    showToast(message, "success");
    setTimeout(() => {
      setCouponLoading(false);
      setPaymentLoading(false);
      window.location.reload();
    }, 500);
  }, [stopStatusPolling]);

  const refreshPurchaseStatus = useCallback(async () => {
    if (!courseDetails?._id) return false;
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/course/web/overview`,
        "POST",
        JSON.stringify({ id: courseDetails._id }),
        { "Content-Type": "application/json" }
      );
      const purchase = data?.purchase_data;
      if (purchase?.payment_status !== "success" || purchase.course_expired) {
        return false;
      }
      const stillRestricted =
        Array.isArray(purchase.unlocked_module_ids) &&
        purchase.unlocked_module_ids.length > 0;
      if (buyFullCourse && (stillRestricted || purchase.metadata?.buy_full_course)) {
        return false;
      }
      completePurchase();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, [USER_BASE_URL, VERSION, courseDetails?._id, buyFullCourse, completePurchase]);

  const startStatusPolling = () => {
    stopStatusPolling();
    refreshPurchaseStatus();
    pollTimerRef.current = setInterval(async () => {
      pollAttemptsRef.current += 1;
      const done = await refreshPurchaseStatus();
      if (done || pollAttemptsRef.current >= 72) {
        stopStatusPolling();
        if (!done && pollAttemptsRef.current >= 72) {
          setPaymentLoading(false);
          setCouponLoading(false);
        }
      }
    }, 5000);
  };

  useEffect(() => {
    return () => stopStatusPolling();
  }, [stopStatusPolling]);

  useEffect(() => {
    if (!open) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshPurchaseStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [open, refreshPurchaseStatus]);

  const handleCoupon = async () => {
    setDiscount(0);
    setCouponLoading(true);
    setAppliedCoupon("");
    setCouponError("");
    setCouponSuccess("");
    if (!discountText) {
      setCouponLoading(false);
      return;
    }
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/coupon/web/`,
        "POST",
        JSON.stringify({ coupon: discountText, course_id: courseDetails._id }),
        { "Content-Type": "application/json" }
      );
      setCouponLoading(false);
      if (data) {
        const nextDiscount =
          data.type === "amount"
            ? data.amount
            : Math.floor((courseDetails.price * data.amount) / 100);
        setDiscount(Math.min(nextDiscount, courseDetails.price));
        const unlockedCount = normalizeModuleIds(
          data.unlocked_modules || data.unlocked_module_ids
        ).length;
        let successText =
          data.type === "amount"
            ? `You got ₹${formatIndianMoney(data.amount)} off for this course`
            : `You got ${data.amount}% off for this course`;
        if (unlockedCount > 0) {
          successText += `\nThis coupon unlocks ${unlockedCount} module(s). Remaining modules need a full course purchase.`;
        }
        setCouponSuccess(successText);
        setAppliedCoupon(discountText);
      } else {
        setCouponError("Invalid Coupon!");
      }
    } catch (err) {
      console.log(err);
      setCouponLoading(false);
      if (err.type === "version_reloading") {
        return;
      }
      if (err.code && err.code === 406) {
        setCouponError(err.type === "app_update" ? "Please refresh this page" : "Account Blocked");
      } else if (err.code && err.code === 404) {
        setCouponError("Invalid Coupon!");
      } else if (err.code && err.code === 400) {
        setCouponError(err.message || "Invalid Coupon!");
      } else {
        setCouponError("Server Error");
      }
    }
  };

  const handleCouponDelete = () => {
    setDiscount(0);
    setDiscountText("");
    setCouponLoading(false);
    setAppliedCoupon("");
    setCouponError("");
    setCouponSuccess("");
  };

  const handleBuy = async () => {
    setPaymentLoading(true);

    try {
      const json_data = { course_id: courseDetails._id };
      if (buyFullCourse) {
        json_data.buy_full_course = true;
      } else if (appliedCoupon && appliedCoupon.length !== 0) {
        json_data.coupon = appliedCoupon;
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/course/web/purchase`,
        "POST",
        JSON.stringify(json_data),
        { "Content-Type": "application/json" }
      );

      if (!data) {
        setPaymentLoading(false);
        showToast("Error initiating payment, please refresh the page and try again!", "error");
        return;
      }

      if (isFreeCheckout(data, { buyFullCourse })) {
        completePurchase();
        return;
      }

      if (!data.order_id || !data.razorpay_id) {
        setPaymentLoading(false);
        showToast("Error initiating payment, please refresh the page and try again!", "error");
        return;
      }

      const options = {
        key: data.razorpay_id,
        amount: razorpayAmountPaise(data, courseDetails.price),
        currency: "INR",
        name: "G9 Lexia",
        description: "Payment for " + courseDetails.name + ", Receipt - " + data.receipt_number,
        order_id: data.order_id,
        image: "/assets/images/g9_edu.png",
        handler: async function (response) {
          try {
            const verifyRes = await apiFetch(
              `${USER_BASE_URL}/${VERSION}/course/web/verify-payment`,
              "POST",
              JSON.stringify({
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                id: data._id,
              }),
              { "Content-Type": "application/json" }
            );

            if (verifyRes) {
              completePurchase();
            } else {
              showToast("Checking payment status...", "success");
              startStatusPolling();
            }
          } catch (err) {
            console.error(err);
            showToast("Checking payment status...", "success");
            startStatusPolling();
          }
        },
        modal: {
          ondismiss: function () {
            showToast("Checking payment status...", "success");
            setPaymentLoading(false);
            startStatusPolling();
          },
        },
        theme: {
          color: "#262261",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      if (err.type === "version_reloading") return;
      setPaymentLoading(false);
      if (err.code === 200) {
        completePurchase("Course already purchased!");
        return;
      }
      showToast(err.message || "Server Error", "error");
    }
  };

  return (
    <>
      {open && <div className="payment-overlay" onClick={onClose} />}

      <div className={`payment-sheet ${open ? "show" : ""}`}>
        <div className="handle-bar" />

        <button className="close-btn" onClick={onClose}>
          <img src="/assets/images/cross.png" alt="close"></img>
        </button>

        <h2 className="sheet-title">
          {buyFullCourse ? "Buy Full Course" : "Payment Overview"}
        </h2>

        <hr className="hr-seperator"></hr>

        <div className="section">
          <h3 className="sub-heading">Course Selected</h3>
          <div className="course-card">
            <img
              src={courseDetails.banner_image.url}
              alt={courseDetails.name}
              className="course-thumb"
            />
            <div className="course-info">
              <p className="course-name">{courseDetails.name}</p>
              <p className="course-lang">Mode of Language: {courseDetails.teaching_language}</p>
              <p className="course-price">₹{formatIndianMoney(courseDetails.price)}</p>
            </div>
          </div>
        </div>

        {buyFullCourse ? (
          <div className="section">
            <p className="buy-full-note">
              This payment unlocks the remaining modules of this course.
            </p>
          </div>
        ) : (
          <div className="section">
            <h3>Apply Coupon</h3>
            <div className="coupon-row">
              <input
                type="text"
                placeholder="WELCOME G9"
                className="coupon-input"
                value={discountText}
                disabled={couponLoading || paymentLoading}
                onChange={(e) => {
                  const value = e.target.value;
                  setCouponError("");
                  setCouponSuccess("");
                  setAppliedCoupon("");
                  setDiscount(0);
                  setDiscountText(value);
                }}
              />
              {(appliedCoupon && appliedCoupon.length !== 0) ? (
                <button
                  className="delete-btn"
                  onClick={handleCouponDelete}
                  disabled={paymentLoading}
                >
                  <img src="/assets/images/trash.svg" alt="delete"></img>
                </button>
              ) : (
                <button
                  disabled={!discountText.trim() || couponLoading || paymentLoading}
                  className="apply-btn"
                  onClick={handleCoupon}
                >
                  {couponLoading ? <span className="loader"></span> : "Apply"}
                </button>
              )}
            </div>
            {(couponError && couponError.length !== 0) && (
              <span className="coupon-error">{couponError}</span>
            )}
            {(couponSuccess && couponSuccess.length !== 0) && (
              <span className="coupon-success">{couponSuccess}</span>
            )}
          </div>
        )}

        <div className="section summary">
          <h3>Summary</h3>
          <div className="overall-summary">
            <div className="row">
              <span>Subtotal</span>
              <span>₹{formatIndianMoney(courseDetails.price)}</span>
            </div>
            {!buyFullCourse && (
              <div className="row">
                <span>Discount</span>
                <span>-₹{formatIndianMoney(discount)}</span>
              </div>
            )}
            <hr />
            <div className="row total">
              <strong>Total</strong>
              <strong>₹{formatIndianMoney(payable)}</strong>
            </div>
          </div>
        </div>

        <button className="pay-btn" disabled={paymentLoading} onClick={handleBuy}>
          {paymentLoading ? (
            <span className="loader"></span>
          ) : payable < 1 ? (
            "Get Course"
          ) : (
            "Pay Now"
          )}
        </button>
      </div>
    </>
  );
}
